import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ScrollText, HardHat, Construction, ShieldAlert, Hammer, Globe, Scale } from 'lucide-react';

const Notch = ({ corner }) => (
  <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    transition={{ duration: 0.5, delay: 0.3 }}
    className={`absolute w-2 h-2 md:w-3 md:h-3 border-orange-500 z-10 
      ${corner === 'top-left' ? 'top-0 left-0 border-t-2 border-l-2' : ''}
      ${corner === 'top-right' ? 'top-0 right-0 border-t-2 border-r-2' : ''}
      ${corner === 'bottom-left' ? 'bottom-0 left-0 border-b-2 border-l-2' : ''}
      ${corner === 'bottom-right' ? 'bottom-0 right-0 border-b-2 border-r-2' : ''}
    `}
  />
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.3 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const TermsSection = ({ icon: Icon, title, children }) => (
  <motion.div 
    variants={itemVariants} 
    className="bg-white p-5 md:p-7 rounded-xl md:rounded-2xl shadow-sm border border-slate-100 mb-4 md:mb-6 relative group"
  >
    <Notch corner="top-left" />
    <Notch corner="bottom-right" />
    
    <div className="flex flex-row items-start gap-4 md:gap-5 relative z-10">
      <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-slate-950 flex items-center justify-center flex-shrink-0">
        <Icon className="text-orange-500" size={20} />
      </div>
      <div className="flex-1">
        <h2 className="text-lg md:text-xl font-black text-slate-950 uppercase italic tracking-tighter mb-2 md:mb-4">
          {title}
        </h2>
        <div className="text-slate-600 text-xs md:text-sm leading-relaxed space-y-2 md:space-y-3 font-medium">
          {children}
        </div>
      </div>
    </div>
  </motion.div>
);

const TermsAndConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Marvel Sato | Terms of Service";
  }, []);

  return (
    <div className="bg-[#f8fafc] min-h-screen font-sans relative overflow-x-hidden">
      
      {/* Blueprint Laser Scan Animation */}
      <motion.div
        initial={{ y: '0%' }}
        animate={{ y: '100%' }}
        transition={{ duration: 1.5, ease: 'easeInOut' }}
        className="fixed inset-x-0 top-0 h-1 bg-orange-500/80 z-[60] pointer-events-none"
      />

      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#1e293b 0.5px, transparent 0.5px)', backgroundSize: '16px 16px' }} />

      {/* Hero Section */}
      <div className="bg-slate-950 pt-12 pb-14 md:pt-16 md:pb-20 px-5 md:px-6 relative overflow-hidden border-b-4 border-orange-500">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6 md:gap-4">
          <div className="relative z-10">
            <h1 className="text-3xl md:text-5xl font-black text-white uppercase italic tracking-tighter mb-1">
              Terms <span className="text-orange-500">&</span> Agreement
            </h1>
            <p className="text-slate-400 font-bold tracking-[0.2em] uppercase text-[9px] md:text-xs">
              MARVEL ENGINEERING PRIVATE LIMITED
            </p>
          </div>
          
          <div className="flex items-center self-start md:self-center gap-3 bg-slate-800/50 px-4 py-2.5 md:px-5 md:py-3 rounded-full border border-slate-700">
            <ScrollText className="text-orange-500" size={16} />
            <div className="flex flex-col">
                <p className="text-[8px] text-slate-500 font-black uppercase tracking-widest leading-none">Version</p>
                <p className="text-white font-mono text-[10px] md:text-[11px] uppercase font-bold">2026.1-B</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Container */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-4xl mx-auto px-5 md:px-6 py-8 md:py-16 relative z-10"
      >
        <p className="font-mono text-[10px] md:text-[11px] text-slate-400 mb-6 md:mb-8 italic uppercase tracking-tighter">
            Last Modified: March 11, 2026
        </p>

        <TermsSection icon={Construction} title="1. Acceptance of Terms">
          <p>
            By accessing the Marvel Sato digital catalog and machinery services, you agree to be bound by these Terms of Agreement. If you are representing a company, you affirm that you have the legal authority to bind said entity to these protocols.
          </p>
        </TermsSection>

        <TermsSection icon={HardHat} title="2. Professional Use Only">
          <p>
            The content on `marvelsato.com`, including crane specifications, hydraulic load charts, and technical CAD previews, is provided for professional industrial evaluation. 
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Unauthorized reproduction of engineering designs is strictly prohibited.</li>
            <li>Users may not scrape data for use in competing machinery databases.</li>
          </ul>
        </TermsSection>

        <motion.div variants={itemVariants} className="bg-orange-500 p-6 rounded-xl md:rounded-2xl mb-6 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-20"><Scale size={60} className="text-white"/></div>
            <h2 className="text-lg md:text-xl font-black text-slate-950 uppercase italic tracking-tighter mb-2">
                3. Governing Law
            </h2>
            <p className="text-slate-900 text-xs md:text-sm font-semibold leading-relaxed relative z-10">
                These terms are governed by the laws of the Republic of India. Any disputes arising from the use of this site or our machinery services shall be subject to the exclusive jurisdiction of the courts in Bengaluru, Karnataka.
            </p>
        </motion.div>

        <TermsSection icon={Globe} title="4. Modifications">
          <p>
            Marvel Sato reserves the right to update these terms as our engineering capabilities and legal requirements evolve. Continued use of the platform after updates constitutes acceptance of the modified protocol.
          </p>
        </TermsSection>
        
      </motion.div>

      <footer className="py-8 md:py-12 bg-white text-center border-t border-slate-100 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-slate-500 text-[9px] md:text-[10px] font-black uppercase tracking-widest leading-relaxed">
            Marvel Engineering Private Limited <br/>
            &copy; 2026 | All Technical Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;